import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useNotification } from "app/NotificationProvider";
import { BORDER_RADIUS } from "app/theme";
import { AnalyticsEvents, analytics } from "common/analytics";
import { useTranslation } from "common/locales";
import { queryKeys } from "common/queryKeys";
import { Base64 } from "js-base64";
import { FileEntry } from "model";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  getAllFileEntriesForUser,
  getSingleFileUploadViaFileEntryApi,
} from "services";
import { useAppStore } from "stores";
import { downloadFileHelper } from "../fileManagement/downloadFileHelper";

function useMyFileEntries() {
  const user = useAppStore((state) => state.user);
  return useQuery({
    queryKey: queryKeys.fileEntries.byUser(user?.guid ?? ""),
    queryFn: async () => {
      const entries = await getAllFileEntriesForUser({});
      return entries.sort(
        (a, b) =>
          Date.parse(b.creationDate ?? "") - Date.parse(a.creationDate ?? "")
      );
    },
    enabled: !!user?.guid,
  });
}

function isPdf(fileName?: string): boolean {
  return !!fileName && fileName.toLowerCase().endsWith(".pdf");
}

function PdfThumbnail({ fileEntryGuid }: { fileEntryGuid: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function render() {
      try {
        const pdfjsLib = await import("pdfjs-dist");
        pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
          "pdfjs-dist/build/pdf.worker.min.mjs",
          import.meta.url
        ).toString();

        const fileUpload = await getSingleFileUploadViaFileEntryApi({
          guid: fileEntryGuid,
        });

        const data = Base64.toUint8Array(fileUpload.file);
        const pdf = await pdfjsLib.getDocument({ data }).promise;
        const page = await pdf.getPage(1);

        if (cancelled) return;

        const canvas = canvasRef.current;
        if (!canvas) return;

        const viewport = page.getViewport({ scale: 1 });
        const targetWidth = 280;
        const scale = targetWidth / viewport.width;
        const scaledViewport = page.getViewport({ scale });

        canvas.width = scaledViewport.width;
        canvas.height = scaledViewport.height;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        await page.render({
          canvas,
          canvasContext: ctx,
          viewport: scaledViewport,
        }).promise;
        setLoading(false);
      } catch {
        if (!cancelled) {
          setError(true);
          setLoading(false);
        }
      }
    }

    void render();
    return () => {
      cancelled = true;
    };
  }, [fileEntryGuid]);

  if (error) {
    return (
      <Box
        sx={{
          width: "100%",
          height: 160,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f4f4f5",
        }}
      >
        <PictureAsPdfIcon sx={{ fontSize: 48, color: "#d4d4d8" }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: 160,
        overflow: "hidden",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        backgroundColor: "#fafafa",
        position: "relative",
      }}
    >
      {loading && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={24} sx={{ color: "#a1a1aa" }} />
        </Box>
      )}
      <canvas
        ref={canvasRef}
        style={{
          maxWidth: "100%",
          height: "auto",
          display: loading ? "none" : "block",
        }}
      />
    </Box>
  );
}

function FileIcon() {
  return (
    <Box
      sx={{
        width: "100%",
        height: 160,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f4f4f5",
      }}
    >
      <InsertDriveFileIcon sx={{ fontSize: 48, color: "#d4d4d8" }} />
    </Box>
  );
}

export function ReportViewerDialog({
  open,
  file,
  onClose,
}: {
  open: boolean;
  file: FileEntry | null;
  onClose: () => void;
}) {
  const [loading, setLoading] = useState(true);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const { notify } = useNotification();

  useEffect(() => {
    if (!open || !file) return;
    let cancelled = false;
    setLoading(true);
    setBlobUrl(null);

    async function loadFile() {
      try {
        const fileUpload = await getSingleFileUploadViaFileEntryApi({
          guid: file?.guid ?? "",
        });

        if (cancelled) return;

        const data = Base64.toUint8Array(fileUpload.file);
        const mimeType = isPdf(file?.fileName)
          ? "application/pdf"
          : "application/octet-stream";
        const blob = new Blob([new Uint8Array(data)], { type: mimeType });
        const url = URL.createObjectURL(blob);
        setBlobUrl(url);
        setLoading(false);
      } catch {
        if (!cancelled) {
          setLoading(false);
          notify("Error loading file", "error");
        }
      }
    }

    void loadFile();
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [open, file?.guid]);

  const handleClose = () => {
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    setBlobUrl(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { height: "90vh" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: 1.5,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            mr: 2,
          }}
        >
          {file?.fileName || file?.fileDescription || "Report"}
        </Typography>
        <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
          <IconButton
            size="small"
            onClick={() => {
              if (file) void downloadFileHelper(file.guid, notify);
            }}
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column" }}>
        {loading ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : blobUrl ? (
          <iframe
            src={blobUrl}
            title={file?.fileName || "Report viewer"}
            style={{
              flex: 1,
              width: "100%",
              border: "none",
            }}
          />
        ) : (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography color="text.secondary">
              Unable to load file preview
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function ReportCard({
  file,
  onPreview,
}: {
  file: FileEntry;
  onPreview: (file: FileEntry) => void;
}) {
  const { notify } = useNotification();
  const { t } = useTranslation();
  const pdf = isPdf(file.fileName);

  const formatDate = (dateString?: string) => {
    if (!dateString) return "";
    try {
      return new Date(dateString).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "";
    }
  };

  const handleClick = useCallback(() => {
    analytics.track(AnalyticsEvents.REPORT_PREVIEWED, {
      fileType: file.fileUploadType,
    });
    onPreview(file);
  }, [file, onPreview]);

  return (
    <Box
      onClick={handleClick}
      sx={{
        backgroundColor: "white",
        borderRadius: BORDER_RADIUS.medium + "px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
        overflow: "hidden",
        cursor: "pointer",
        transition: "box-shadow 0.2s, transform 0.15s",
        "&:hover": {
          boxShadow: "0 4px 16px rgba(0, 0, 0, 0.12)",
          transform: "translateY(-1px)",
        },
      }}
    >
      {pdf ? <PdfThumbnail fileEntryGuid={file.guid} /> : <FileIcon />}

      <Box sx={{ px: 2, py: 1.5 }}>
        <Typography
          variant="caption"
          sx={{
            color: "#71717a",
            display: "block",
            mb: 0.25,
          }}
        >
          {t(`fileEntries.fileUploadType.label.${file.fileUploadType}`)}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: "#18181b",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            mb: 0.5,
          }}
        >
          {file.fileName || file.fileDescription || "-"}
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="caption" sx={{ color: "#a1a1aa" }}>
            {formatDate(file.creationDate)}
          </Typography>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              analytics.track(AnalyticsEvents.REPORT_DOWNLOADED, {
                fileType: file.fileUploadType,
              });
              void downloadFileHelper(file.guid, notify);
            }}
            sx={{
              padding: "4px",
              "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
            }}
          >
            <DownloadIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}

export default function ReportsSection() {
  const { data: files, isLoading } = useMyFileEntries();
  const [previewFile, setPreviewFile] = useState<FileEntry | null>(null);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
          gap: 2,
        }}
      >
        {[1, 2].map((i) => (
          <Skeleton
            key={i}
            variant="rounded"
            height={220}
            sx={{ borderRadius: BORDER_RADIUS.medium + "px" }}
          />
        ))}
      </Box>
    );
  }

  if (!files || files.length === 0) {
    return (
      <Box
        sx={{
          backgroundColor: "white",
          borderRadius: BORDER_RADIUS.medium + "px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
          p: 4,
          textAlign: "center",
        }}
      >
        <InsertDriveFileIcon sx={{ fontSize: 40, color: "#d4d4d8", mb: 1 }} />
        <Typography variant="body2" sx={{ color: "#71717a" }}>
          No reports available yet. Reports will appear here once they have been
          uploaded.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
          gap: 2,
        }}
      >
        {files.map((file) => (
          <ReportCard key={file.guid} file={file} onPreview={setPreviewFile} />
        ))}
      </Box>

      <ReportViewerDialog
        open={previewFile !== null}
        file={previewFile}
        onClose={() => setPreviewFile(null)}
      />
    </>
  );
}
